import { getFootballSeasonYear } from '../../utils/season';
import { logger } from "./logger";
// src/utils/keyUtils.js
// 🗝️ KEY GENERATION UTILITIES - Prevents React duplicate key errors

// Remove React imports since this is a pure JS utility file
let keyCounter = 0;

/**
 * Generate guaranteed unique React keys for components
 * @param {string} prefix - Key prefix for identification
 * @param {string|number} additionalData - Additional data to make key more specific
 * @returns {string} Unique key safe for React
 */
export const generateUniqueKey = (prefix = 'key', additionalData = '') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  const counter = ++keyCounter;
  const hash = additionalData ? btoa(String(additionalData)).substr(0, 8) : '';
  
  return `${prefix}_${timestamp}_${counter}_${random}_${hash}`.replace(/[^a-zA-Z0-9_]/g, '_');
};

/**
 * Generate stable keys based on data content
 * Use when you need consistent keys across renders
 * @param {any} data - Data to generate key from
 * @param {string} prefix - Key prefix
 * @returns {string} Stable key based on data content
 */
export const generateStableKey = (data, prefix = 'stable') => {
  if (!data) return generateUniqueKey(prefix);
  
  // Create stable hash from data
  const dataString = typeof data === 'string' ? data : JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < dataString.length; i++) {
    const char = dataString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return `${prefix}_${Math.abs(hash)}_${dataString.length}`;
};

/**
 * Group-specific key generation (fixes your specific duplicate key error)
 * @param {Object} groupData - Group data object
 * @param {Object} memberData - Member data object
 * @param {number} index - Array index as fallback
 * @returns {string} Unique group member key
 */
export const generateGroupKey = (groupData, memberData, index) => {
  const groupId = (groupData?.id || groupData?.groupId || 'unknown').toString().replace(/[^a-zA-Z0-9]/g, '_');
  const memberId = (memberData?.id || memberData?.uid || memberData?.userId || memberData?.participantName || index).toString().replace(/[^a-zA-Z0-9]/g, '_');
  const team = (groupData?.team || memberData?.team || 'notable').toString().replace(/[^a-zA-Z0-9]/g, '_');
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 5);
  
  return `group_${groupId}_member_${memberId}_${team}_${timestamp}_${random}`;
};

/**
 * Schedule/Game specific key generation
 * @param {Object} gameData - Game data object
 * @param {number} index - Game index
 * @returns {string} Unique game key
 */
export const generateGameKey = (gameData, index) => {
  const opponent = (gameData?.opponent || 'opponent').toString().replace(/[^a-zA-Z0-9]/g, '_');
  const date = gameData?.date ? new Date(gameData.date).getTime() : Date.now();
  const gameId = gameData?.id || index;
  const random = Math.random().toString(36).substr(2, 4);
  
  return `game_${gameId}_${opponent}_${date}_${random}`;
};

/**
 * Position/Grade specific key generation
 * @param {string} position - Position name
 * @param {string} type - Grade type (position, coach, etc.)
 * @param {number} index - Index as fallback
 * @returns {string} Unique position key
 */
export const generatePositionKey = (position, type = 'grade', index = 0) => {
  const safePosition = position.toString().replace(/[^a-zA-Z0-9]/g, '_');
  const safeType = type.toString().replace(/[^a-zA-Z0-9]/g, '_');
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 4);
  
  return `${safeType}_${safePosition}_${index}_${timestamp}_${random}`;
};

/**
 * Analysis/Result specific key generation
 * @param {Object} analysisData - Analysis data
 * @param {string} type - Analysis type
 * @param {number} index - Index fallback
 * @returns {string} Unique analysis key
 */
export const generateAnalysisKey = (analysisData, type = 'analysis', index = 0) => {
  const userId = (analysisData?.userId || analysisData?.uid || 'user').toString().replace(/[^a-zA-Z0-9]/g, '_');
  const team = (analysisData?.team || 'team').toString().replace(/[^a-zA-Z0-9]/g, '_');
  const season = analysisData?.season || getFootballSeasonYear();
  const timestamp = analysisData?.submittedAt || analysisData?.createdAt || Date.now();
  const random = Math.random().toString(36).substr(2, 4);
  
  return `${type}_${userId}_${team}_${season}_${timestamp}_${index}_${random}`;
};

/**
 * React Hook: Generate stable component keys
 * Note: This should be used in .jsx files with React imported
 * @param {any} data - Data to base key on
 * @param {string} prefix - Key prefix
 * @returns {string} Stable key for component
 */
export const useStableKey = (data, prefix = 'component') => {
  // For use in React components - returns a memoized stable key
  // This is a factory function that can be used with React.useMemo
  return generateStableKey(data, prefix);
};

/**
 * React Hook: Generate unique keys for arrays/lists
 * Note: This should be used in .jsx files with React imported
 * @param {number} arrayLength - Length of array needing keys
 * @param {string} prefix - Key prefix
 * @returns {string[]} Array of unique keys
 */
export const useUniqueKeys = (arrayLength, prefix = 'item') => {
  // For use in React components - returns an array of unique keys
  // This is a factory function that can be used with React.useMemo
  return Array.from({ length: arrayLength }, (_, i) => generateUniqueKey(prefix, i));
};

/**
 * React Hook: Get a unique ID that persists across renders
 * Note: This should be used in .jsx files with React imported
 * @param {string} prefix - ID prefix
 * @returns {string} Persistent unique ID
 */
export const useUniqueId = (prefix = 'id') => {
  // For use in React components - returns a persistent unique ID
  // This is a factory function that can be used with React.useRef
  return generateUniqueKey(prefix);
};

/**
 * Higher-order component to automatically add unique keys to array children
 * Note: This should be used in .jsx files, not .js files
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Component with automatic key generation
 */
export const withUniqueKeys = (Component) => {
  // This function should be used in JSX files, not JS files
  // We'll return a factory function instead of JSX
  return function ComponentWithUniqueKeys(props) {
    // Return props for the component with unique keys added
    const uniqueId = generateUniqueKey('component');
    
    // Ensure any arrays in props get unique keys
    const propsWithKeys = { ...props };
    
    // Find array props and add unique keys
    Object.keys(propsWithKeys).forEach(key => {
      if (Array.isArray(propsWithKeys[key])) {
        propsWithKeys[key] = propsWithKeys[key].map((item, index) => ({
          ...item,
          __uniqueKey: item.__uniqueKey || generateUniqueKey(`${key}_item`, index)
        }));
      }
    });
    
    // Return the enhanced props and component data
    return {
      props: propsWithKeys,
      componentId: uniqueId,
      // Note: Actual JSX rendering should be done in the consuming component
    };
  };
};

/**
 * Utility to clean/sanitize strings for use in keys
 * @param {string} str - String to clean
 * @returns {string} Cleaned string safe for keys
 */
export const sanitizeForKey = (str) => {
  if (!str) return 'empty';
  return str.toString().replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
};

/**
 * Debug utility to check for duplicate keys in DOM
 * Only available in development
 * @returns {Object} Report of duplicate keys found
 */
export const checkForDuplicateKeys = () => {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('Key checking only available in development mode');
    return {};
  }
  
  const keys = new Map();
  const duplicates = [];
  
  // Check all elements with React keys
  document.querySelectorAll('[data-reactroot] *').forEach(el => {
    const key = el.getAttribute('data-reactid') || el.getAttribute('key');
    if (key) {
      if (keys.has(key)) {
        duplicates.push({
          key,
          elements: [keys.get(key), el]
        });
      } else {
        keys.set(key, el);
      }
    }
  });
  
  if (duplicates.length > 0) {
    console.warn('🚨 Duplicate keys found:', duplicates);
    return { duplicates, totalKeys: keys.size };
  } else {
    logger.success('✅ No duplicate keys found');
    return { duplicates: [], totalKeys: keys.size };
  }
};

// Export all functions as default object for convenience
export default {
  generateUniqueKey,
  generateStableKey,
  generateGroupKey,
  generateGameKey,
  generatePositionKey,
  generateAnalysisKey,
  useStableKey,
  useUniqueKeys,
  useUniqueId,
  withUniqueKeys,
  sanitizeForKey,
  checkForDuplicateKeys
};